import React, { useState } from "react";
import { unsubscribePerformer, prefetchProfile } from "../api";
import { loadSubscriptions } from "../utils/subscriptionsStore";
import useNsfwMode from "../utils/useNsfwMode";
import { isRostered } from "../utils/rosteredNames";

// Venue slugs are stored on the subscription row; map them to the labels used
// in the roster tabs so the list reads the same as the rest of the site.
export const VENUE_LABELS = {
  marrickville: "Marrickville",
  gladesville: "Gladesville",
  cleveland: "Cleveland St",
  elizabeth: "Elizabeth St",
};

export const venueLabel = (slug) =>
  VENUE_LABELS[slug] || (slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : "Unknown");

// The list of performers the signed-in user is following, grouped by venue.
// Removal works entirely from the stored subscription - it never consults the
// current roster - so you can unfollow someone who isn't working today.
function SubscriptionList({ subscriptions, onChanged, rosteredKeys }) {
  const [removing, setRemoving] = useState({});
  // Profile details/photos sit behind the same NSFW toggle the roster tables use.
  const isNsfw = useNsfwMode();

  // Opens the shared ProfileModal, which App renders outside <Routes> and drives
  // from the URL hash - so the same deep link works from here and from the roster.
  const openProfile = (venue, performerName) => {
    window.location.hash = `#profile-${venue}-${performerName.replace(/[\s-]+$/, "")}`;
  };

  const handleRemove = async (venue, performerName) => {
    const key = `${venue}::${performerName}`;
    setRemoving((prev) => ({ ...prev, [key]: true }));
    try {
      await unsubscribePerformer(venue, performerName);
      // Keep the shared cache (and every bell icon in the roster) in sync.
      await loadSubscriptions();
      if (onChanged) await onChanged();
    } finally {
      setRemoving((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  if (!subscriptions || subscriptions.length === 0) {
    return (
      <p className="text-muted small mb-0">
        You're not following anyone yet. Click the 🔕 bell next to a name in any roster to
        get notified when they're on tomorrow's list.
      </p>
    );
  }

  const byVenue = subscriptions.reduce((acc, sub) => {
    (acc[sub.venue] = acc[sub.venue] || []).push(sub);
    return acc;
  }, {});

  const venues = Object.keys(byVenue).sort((a, b) =>
    venueLabel(a).localeCompare(venueLabel(b))
  );

  return (
    <div>
      {venues.map((venue) => (
        <div key={venue} className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <strong className="small text-uppercase text-muted">{venueLabel(venue)}</strong>
            <span className="badge bg-secondary">{byVenue[venue].length}</span>
          </div>
          <ul className="list-group">
            {byVenue[venue]
              .slice()
              .sort((a, b) => a.performer_name.localeCompare(b.performer_name))
              .map((sub) => {
                const key = `${sub.venue}::${sub.performer_name}`;
                const busy = Boolean(removing[key]);
                // Only link to a profile while the performer is actually rostered -
                // otherwise there may be no stored details to show.
                const linkable =
                  isNsfw && isRostered(rosteredKeys, sub.venue, sub.performer_name);
                return (
                  <li
                    key={key}
                    className="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <span>
                      🔔{" "}
                      {linkable ? (
                        <a
                          href={`#profile-${sub.venue}-${sub.performer_name}`}
                          onMouseEnter={() => prefetchProfile(sub.performer_name, sub.venue)}
                          onFocus={() => prefetchProfile(sub.performer_name, sub.venue)}
                          onTouchStart={() => prefetchProfile(sub.performer_name, sub.venue)}
                          onClick={(e) => {
                            e.preventDefault();
                            openProfile(sub.venue, sub.performer_name);
                          }}
                        >
                          {sub.performer_name}
                        </a>
                      ) : (
                        sub.performer_name
                      )}
                      {linkable && (
                        <span className="badge bg-success ms-2" style={{ fontSize: "0.7em" }}>
                          on roster
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleRemove(sub.venue, sub.performer_name)}
                      disabled={busy}
                      aria-label={`Stop notifications for ${sub.performer_name} at ${venueLabel(
                        sub.venue
                      )}`}
                    >
                      {busy ? "Removing..." : "Remove"}
                    </button>
                  </li>
                );
              })}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default SubscriptionList;
